import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { api, fmt } from "@/api";
import { toast } from "sonner";
import { CreditCard, Wallet, IndianRupee } from "lucide-react";

const STATES = ["Andhra Pradesh","Arunachal Pradesh","Assam","Bihar","Chhattisgarh","Delhi","Goa","Gujarat","Haryana","Himachal Pradesh","Jharkhand","Karnataka","Kerala","Madhya Pradesh","Maharashtra","Manipur","Meghalaya","Mizoram","Nagaland","Odisha","Punjab","Rajasthan","Sikkim","Tamil Nadu","Telangana","Tripura","Uttar Pradesh","Uttarakhand","West Bengal"];

export default function Checkout() {
  const { user, authLoading, cart, subtotal, clearCart } = useApp();
  const loc = useLocation();
  const nav = useNavigate();
  const { discount = 0, couponCode = "" } = loc.state || {};
  const total = quote.total;
  const shipping = quote.shipping;

  const [addr, setAddr] = useState({ full_name:"", phone:"", line1:"", line2:"", landmark:"", city:"", state:"Maharashtra", pincode:"", is_default:true });
  const [method, setMethod] = useState("cod");
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState({ subtotal, shipping: 0, cod_fee: 0, online_discount: 0, total: subtotal });

  // Live server-side quote when payment method or cart changes
  useEffect(() => {
    if (cart.length === 0) return;
    const items = cart.map(c => ({ product_id: c.product_id, price: c.price, qty: c.qty, kit: c.kit || false }));
    api.post("/pricing/quote", { items, payment_method: method }).then(({data}) => setQuote(data)).catch(()=>{});
  }, [method, cart]);

  useEffect(() => {
    if (!user) return;
    setAddr(a => ({ ...a, full_name: user.name || "", phone: user.phone || "" }));
    api.get("/addresses").then(({data}) => {
      if (data.length) { const d = data.find(x=>x.is_default) || data[0]; setAddr(a=>({...a, ...d})); }
    });
  }, [user]);

  if (authLoading) return <div className="p-20 text-center text-muted-foreground">Loading...</div>;
  if (!user) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <h1 className="font-serif text-3xl">Please login to continue</h1>
      <Link to={`/login?next=/checkout`} className="pk-btn-primary mt-6 inline-flex">Login</Link>
    </div>
  );
  if (cart.length === 0) return <div className="max-w-3xl mx-auto px-4 py-20 text-center"><h1 className="font-serif text-2xl">Your cart is empty</h1><Link to="/" className="mt-4 pk-btn-primary inline-flex">Home</Link></div>;

  const validate = () => {
    if (!/^[0-9]{10}$/.test(addr.phone)) { toast.error("Enter valid 10-digit mobile"); return false; }
    if (!/^[0-9]{6}$/.test(addr.pincode)) { toast.error("Enter valid 6-digit pincode"); return false; }
    if (!addr.full_name || !addr.line1 || !addr.city) { toast.error("Please fill all required fields"); return false; }
    return true;
  };

  const placeOrder = async () => {
    if (!validate()) return;
    setLoading(true);
    const items = cart.map(c => ({ product_id: c.product_id, name: c.name, price: c.price, qty: c.qty, image: c.image, kit: c.kit || false }));
    const basePayload = { items, address: addr, payment_method: method, coupon_code: couponCode || null, subtotal, shipping, discount, tax: 0, total };
    try {
      if (method === "phonepe") {
        const { data } = await api.post("/payments/phonepe/initiate", basePayload);
        clearCart();
        window.location.assign(data.redirect_url);
        return;
      }
      if (method === "razorpay") {
        // 1) Create Razorpay order on backend
        const { data: rzOrder } = await api.post("/payments/razorpay/order", { amount: total });
        if (rzOrder.demo || !rzOrder.key_id) {
          // Demo mode — no live keys. Simulate success.
          const { data } = await api.post("/orders", { ...basePayload,
            razorpay_order_id: rzOrder.id, razorpay_payment_id: `pay_demo_${Date.now()}`, razorpay_signature: "demo" });
          clearCart(); toast.success(`Order placed! ${data.order_no} (Demo payment)`); nav(`/order/${data.id}`); return;
        }
        // 2) Open Razorpay Checkout modal
        if (!window.Razorpay) { toast.error("Razorpay SDK not loaded"); setLoading(false); return; }
        const options = {
          key: rzOrder.key_id, amount: rzOrder.amount, currency: rzOrder.currency, order_id: rzOrder.id,
          name: "DivineCart", description: `Order for ${cart.length} items`,
          prefill: { name: addr.full_name, email: user.email, contact: addr.phone },
          theme: { color: "#800000" },
          handler: async (rz) => {
            try {
              const { data } = await api.post("/orders", { ...basePayload,
                razorpay_order_id: rz.razorpay_order_id,
                razorpay_payment_id: rz.razorpay_payment_id,
                razorpay_signature: rz.razorpay_signature });
              clearCart(); toast.success(`Payment successful! ${data.order_no}`); nav(`/order/${data.id}`);
            } catch (e) { toast.error("Payment succeeded but order failed. Contact support."); }
          },
          modal: { ondismiss: () => setLoading(false) }
        };
        new window.Razorpay(options).open();
        return;
      }
      // COD path
      const { data } = await api.post("/orders", basePayload);
      clearCart(); toast.success(`Order placed! ${data.order_no}`); nav(`/order/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to place order"); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <h1 className="font-serif text-4xl">Checkout</h1>
      <div className="mt-8 grid md:grid-cols-[1fr_380px] gap-8">
        <div className="space-y-8">
          <div className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-serif text-xl mb-4">Shipping Address</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="chk-name" className="pk-input" placeholder="Full Name" value={addr.full_name} onChange={e=>setAddr({...addr, full_name:e.target.value})}/>
              <input data-testid="chk-phone" className="pk-input" placeholder="Mobile Number" value={addr.phone} onChange={e=>setAddr({...addr, phone:e.target.value.replace(/\D/g,"").slice(0,10)})}/>
              <input data-testid="chk-line1" className="pk-input sm:col-span-2" placeholder="House / Flat No. + Street" value={addr.line1} onChange={e=>setAddr({...addr, line1:e.target.value})}/>
              <input className="pk-input" placeholder="Area / Locality" value={addr.line2} onChange={e=>setAddr({...addr, line2:e.target.value})}/>
              <input className="pk-input" placeholder="Landmark (optional)" value={addr.landmark} onChange={e=>setAddr({...addr, landmark:e.target.value})}/>
              <input data-testid="chk-city" className="pk-input" placeholder="City" value={addr.city} onChange={e=>setAddr({...addr, city:e.target.value})}/>
              <select data-testid="chk-state" className="pk-input" value={addr.state} onChange={e=>setAddr({...addr, state:e.target.value})}>
                {STATES.map(s=><option key={s}>{s}</option>)}
              </select>
              <input data-testid="chk-pincode" className="pk-input" placeholder="Pincode" value={addr.pincode} onChange={e=>setAddr({...addr, pincode:e.target.value.replace(/\D/g,"").slice(0,6)})}/>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-white p-6">
            <h2 className="font-serif text-xl mb-4">Payment Method</h2>
            <div className="space-y-2">
              {[
                {v:"cod", label:"Cash on Delivery (COD)", d:"Pay when you receive", icon: IndianRupee},
                {v:"razorpay", label:"UPI / Cards / Netbanking", d:"Razorpay secure payment", icon: CreditCard},
              ].map(o => (
                <label key={o.v} data-testid={`pay-${o.v}`} className={`flex items-center gap-4 p-4 rounded-xl border cursor-pointer ${method===o.v?"border-primary bg-primary/5":"border-border"}`}>
                  <input type="radio" name="pay" checked={method===o.v} onChange={()=>setMethod(o.v)} className="accent-primary"/>
                  <o.icon className="w-5 h-5 text-primary"/>
                  <div className="flex-1"><div className="font-medium">{o.label}</div><div className="text-xs text-muted-foreground">{o.d}</div></div>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-white p-6 h-fit sticky top-24">
          <h2 className="font-serif text-xl mb-4">Order Summary</h2>
          <div className="space-y-2 max-h-56 overflow-y-auto text-sm">
            {cart.map(c=>(<div key={c.product_id} className="flex justify-between gap-3"><span className="flex-1 truncate">{c.name} × {c.qty}</span><span>{fmt(c.price*c.qty)}</span></div>))}
          </div>
          <div className="border-t border-border mt-4 pt-4 space-y-2 text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>{fmt(quote.subtotal || subtotal)}</span></div>
            {discount>0 && <div className="flex justify-between text-emerald-700"><span>Coupon ({couponCode})</span><span>-{fmt(discount)}</span></div>}
            <div className="flex justify-between"><span>Shipping</span><span>{shipping===0?"FREE":fmt(shipping)}</span></div>
            {quote.cod_fee > 0 && <div className="flex justify-between"><span>COD Fee</span><span>{fmt(quote.cod_fee)}</span></div>}
            {quote.online_discount > 0 && <div className="flex justify-between text-emerald-700"><span>💳 Online Payment 5% OFF</span><span>-{fmt(quote.online_discount)}</span></div>}
          </div>
          <div className="border-t border-border mt-4 pt-4 flex justify-between items-baseline">
            <span className="font-medium">Total</span>
            <span className="text-2xl font-semibold text-primary">{fmt(total)}</span>
          </div>
          <button data-testid="place-order-btn" disabled={loading} onClick={placeOrder} className="pk-btn-primary w-full mt-5">
            {loading ? "Placing..." : `Place Order · ${fmt(total)}`}
          </button>
          <div className="mt-3 text-[10px] text-muted-foreground text-center">By placing order you agree to our Terms & Privacy Policy</div>
        </div>
      </div>
    </div>
  );
}
